import { useState, useEffect, useRef, useMemo } from 'react';
import { venuesApi } from '../api/venues.api';
import { placesApi, type AutocompletePrediction } from '../api/places.api';
import { toast } from '../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import type { Venue, VenueCategory, ItineraryDay, TripDestination } from '../types';
import type { PinnedVenue, PlaceDetail } from '../components/map/VenueDetailCard';
import { dayNumbersByVenue } from '../utils/venueStops';
import { legOfPlace } from '../utils/tripLegs';
import { fitMapTo } from '../utils/mapFit';

// New session token per search session — reused across keystrokes and consumed
// by the paired details lookup, so the whole search bills as ONE Places session.
function genSessionToken(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Stable, so the `filtered` memo doesn't recompute on every render.
const NO_LEGS: readonly TripDestination[] = [];

interface UseVenueMapOptions {
  tripId: string;
  tripDestination: string;
  days: ItineraryDay[];
  /** The trip's destinations (ADR-0030), for the leg filter. Absent = no leg filter. */
  legs?: readonly TripDestination[];
  mapRef: React.RefObject<google.maps.Map | null>;
  mapWrapRef: React.RefObject<HTMLDivElement | null>;
  isLoaded: boolean;
  /** True once the GoogleMap instance has fired onLoad (mapRef is populated). */
  isMapReady: boolean;
  fsContainer: Element;
  /** Trip centre, used to bias place search towards the destination. */
  center: { lat: number; lng: number } | null;
  /** Width in px of the panel floating over the right edge of the map, if open. */
  rightInset: number;
}

export function useVenueMap({
  tripId, tripDestination, days, legs = NO_LEGS, mapRef, mapWrapRef, isLoaded, isMapReady, fsContainer, center, rightInset,
}: UseVenueMapOptions) {
  const { t } = useTranslation();
  const [venues, setVenues]             = useState<Venue[]>([]);
  const [pinned, setPinned]             = useState<PinnedVenue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [geocoding, setGeocoding]       = useState(false);
  const [filter, setFilter]             = useState<'all' | 'pending' | 'visited'>('all');
  // Positive selection: empty = no category filter (every category shows).
  const [selectedCats, setSelectedCats] = useState<Set<VenueCategory>>(new Set());
  // Index into `legs`; null = every leg.
  const [legFilter, setLegFilter]       = useState<number | null>(null);
  const [activePin, setActivePin]       = useState<string | null>(null);
  const [venuePanel, setVenuePanel]     = useState<PinnedVenue | null>(null);
  const [addToItineraryVenue, setAddToItineraryVenue] = useState<Venue | null>(null);
  const [placeDetails, setPlaceDetails] = useState<Record<string, PlaceDetail | null>>({});
  const [detailsLoading, setDetailsLoading] = useState<Set<string>>(new Set());
  const [pinScreenPos, setPinScreenPos] = useState<{ x: number; y: number } | null>(null);
  const [cardDragOffset, setCardDragOffset] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

  const [searchResult, setSearchResult] = useState<{
    name: string; lat: number; lng: number; address: string; mapsUrl: string; placeId: string | null; photos?: string[];
  } | null>(null);
  const [saveOpen, setSaveOpen]         = useState(false);
  const [searchScreenPos, setSearchScreenPos] = useState<{ x: number; y: number } | null>(null);
  const [fsQuery, setFsQuery]           = useState('');
  const [fsPredictions, setFsPredictions] = useState<AutocompletePrediction[]>([]);

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const geocodedRef    = useRef<string>('');
  const fsTokenRef     = useRef<string>(genSessionToken());

  const venueTodays = useMemo(() => dayNumbersByVenue(days, venues), [days, venues]);

  const filtered = useMemo(() =>
    pinned.filter(v =>
      (filter === 'all' ? true : filter === 'visited' ? v.visited : !v.visited) &&
      (selectedCats.size === 0 || selectedCats.has(v.category)) &&
      (legFilter === null || (legOfPlace(legs, venueTodays.get(v.id) ?? [], v) ?? legFilter) === legFilter)
    ),
  [pinned, filter, selectedCats, legFilter, legs, venueTodays]);

  const routeUrl = pinned.filter(v => !v.visited).length > 0
    ? `https://www.google.com/maps/dir/${pinned.filter(v => !v.visited).map(v => `${v.lat},${v.lng}`).join('/')}`
    : null;

  // Load venues
  useEffect(() => {
    venuesApi.list(tripId)
      .then(v => { setVenues(v); setVenuesLoading(false); })
      .catch(() => { toast(t('venues.toast.loadFailed'), 'error'); setVenuesLoading(false); });
  }, [tripId]);

  // Resolve venue coords
  useEffect(() => {
    if (venues.length === 0) { setPinned([]); return; }
    const key = venues.map(v => v.id).sort().join(',');
    if (geocodedRef.current === key) return;
    geocodedRef.current = key;
    setGeocoding(true);
    venuesApi.coords(tripId)
      .then(items => {
        const byId: Record<string, { lat: number; lng: number }> = {};
        for (const c of items) byId[c.id] = { lat: c.lat, lng: c.lng };
        const results: PinnedVenue[] = [];
        for (const v of venues) {
          const c = byId[v.id];
          if (c) results.push({ ...v, lat: c.lat, lng: c.lng });
        }
        setPinned(results);
      })
      .catch(err => { console.warn('[MapTab] failed to load venue coords:', err); setPinned([]); })
      .finally(() => setGeocoding(false));
  }, [venues, tripId]);

  // Fit bounds to the pinned venues once the map instance is ready and geocoding
  // is done. Gated on isMapReady (not just isLoaded) so it also runs when
  // geocoding finished before the GoogleMap's onLoad fired.
  useEffect(() => {
    if (!isLoaded || !isMapReady || !mapRef.current || filtered.length === 0 || geocoding) return;
    fitMapTo(mapRef.current, filtered, rightInset);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geocoding, filter, legFilter, isLoaded, isMapReady, rightInset]);

  // Autocomplete predictions — debounced and proxied through the server so the
  // request is cached/capped/tracked and the session token consolidates billing.
  useEffect(() => {
    const q = fsQuery.trim();
    if (q.length < 2) { setFsPredictions([]); return; }
    const timer = setTimeout(() => {
      // Bias to the trip's own city: without it, searching "Catedral" on a trip
      // to Girona listed the cathedrals of Palma, Sevilla and Barcelona first.
      const bias = center ? `${center.lat},${center.lng}` : undefined;
      placesApi.autocomplete(q, fsTokenRef.current, { bias })
        .then(setFsPredictions)
        .catch(() => setFsPredictions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [fsQuery, center]);

  // Track pin screen position
  useEffect(() => {
    if (!venuePanel || !isLoaded) { setPinScreenPos(null); return; }
    const map = mapRef.current;
    if (!map) return;
    function update() {
      const proj = map!.getProjection();
      const bounds = map!.getBounds();
      if (!proj || !bounds) return;
      const rect = (document.fullscreenElement ?? mapWrapRef.current ?? map!.getDiv()).getBoundingClientRect();
      const zoom = map!.getZoom() ?? 12;
      const scale = 1 << zoom;
      const nw = proj.fromLatLngToPoint(new window.google.maps.LatLng(bounds.getNorthEast().lat(), bounds.getSouthWest().lng()));
      const pt = proj.fromLatLngToPoint(new window.google.maps.LatLng(venuePanel!.lat, venuePanel!.lng));
      if (!nw || !pt) return;
      setPinScreenPos({ x: rect.left + (pt.x - nw.x) * scale, y: rect.top + (pt.y - nw.y) * scale });
    }
    update();
    const listener = window.google.maps.event.addListener(map, 'bounds_changed', update);
    return () => { window.google.maps.event.removeListener(listener); setPinScreenPos(null); };
  }, [venuePanel, isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track search pin screen position
  useEffect(() => {
    if (!searchResult || !isLoaded) { setSearchScreenPos(null); return; }
    const map = mapRef.current;
    if (!map) return;
    function update() {
      const proj = map!.getProjection();
      const bounds = map!.getBounds();
      if (!proj || !bounds) return;
      const rect = (document.fullscreenElement ?? mapWrapRef.current ?? map!.getDiv()).getBoundingClientRect();
      const zoom = map!.getZoom() ?? 12;
      const scale = 1 << zoom;
      const nw = proj.fromLatLngToPoint(new window.google.maps.LatLng(bounds.getNorthEast().lat(), bounds.getSouthWest().lng()));
      const pt = proj.fromLatLngToPoint(new window.google.maps.LatLng(searchResult!.lat, searchResult!.lng));
      if (!nw || !pt) return;
      setSearchScreenPos({ x: rect.left + (pt.x - nw.x) * scale, y: rect.top + (pt.y - nw.y) * scale });
    }
    update();
    const listener = window.google.maps.event.addListener(map, 'bounds_changed', update);
    return () => { window.google.maps.event.removeListener(listener); setSearchScreenPos(null); };
  }, [searchResult, isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset drag offset when panel changes
  useEffect(() => { setCardDragOffset({ dx: 0, dy: 0 }); }, [venuePanel]);

  // Escape closes card / search result
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (venuePanel) { setActivePin(null); setVenuePanel(null); }
      else closeSearchResult();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [venuePanel]); // eslint-disable-line react-hooks/exhaustive-deps

  function computePinScreen(lat: number, lng: number): { x: number; y: number } | null {
    const map = mapRef.current;
    if (!map) return null;
    const proj = map.getProjection();
    const bounds = map.getBounds();
    if (!proj || !bounds) return null;
    const container = document.fullscreenElement ?? mapWrapRef.current ?? map.getDiv();
    const rect = container.getBoundingClientRect();
    const scale = 1 << (map.getZoom() ?? 12);
    const nw = proj.fromLatLngToPoint(new window.google.maps.LatLng(bounds.getNorthEast().lat(), bounds.getSouthWest().lng()));
    const pt = proj.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng));
    if (!nw || !pt) return null;
    return { x: rect.left + (pt.x - nw.x) * scale, y: rect.top + (pt.y - nw.y) * scale };
  }

  function closeSearchResult() {
    setSearchResult(null);
    setFsQuery('');
    searchInputRef.current?.blur();
  }

  /**
   * Photos/rating/hours cost a Place Details (rich) call, so opening a card no
   * longer triggers one: we only auto-load when the server already has them
   * cached (free). Everything else waits for an explicit request from the card
   * — `force: true`.
   */
  function loadVenueDetails(venue: PinnedVenue, opts: { force?: boolean } = {}) {
    if (venue.id in placeDetails) return;
    if (!opts.force && !venue.hasCachedDetails) return;

    setDetailsLoading(prev => new Set(prev).add(venue.id));
    const commit = (detail: PlaceDetail | null) => {
      setPlaceDetails(prev => ({ ...prev, [venue.id]: detail }));
      setDetailsLoading(prev => { const next = new Set(prev); next.delete(venue.id); return next; });
    };
    venuesApi.getDetails(tripId, venue.id)
      .then(d => {
        if (!d) { commit(null); return; }
        commit({ photos: d.photos ?? [], rating: d.rating, userRatingsTotal: d.userRatingsTotal, priceLevel: d.priceLevel, openNow: d.openNow ?? null, openingHoursPeriods: d.openingHoursPeriods, phone: d.phone, website: d.website });
      })
      .catch(() => commit(null));
  }

  function toggleCat(cat: VenueCategory) {
    const usedCats = new Set(pinned.map(v => v.category));
    setSelectedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      // Picking every category is the same as picking none — collapse back to
      // "All" so the bar never shows a filter that filters nothing.
      return next.size === usedCats.size ? new Set() : next;
    });
  }

  function clearCats() {
    setSelectedCats(new Set());
  }

  async function onFsSelect(placeId: string) {
    setFsPredictions([]);
    setFsQuery('');
    // Details go through the server proxy with the same session token, so the
    // autocomplete keystrokes + this lookup close as one billing session.
    const detail = await placesApi.details(placeId, fsTokenRef.current);
    fsTokenRef.current = genSessionToken(); // token consumed → start a fresh session
    if (!detail) return;
    const { lat, lng } = detail;
    setSearchResult({
      name: detail.name,
      lat, lng,
      address: detail.address,
      placeId: detail.placeId,
      // Photos are omitted from this pre-save preview to avoid the pricier
      // Place Details (photos) SKU on every search; they load lazily from the
      // saved venue's detail card instead.
      photos: [],
      mapsUrl: detail.mapsUrl
        ?? `https://www.google.com/maps/search/?q=${lat},${lng}`,
    });
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
    setTimeout(() => mapRef.current?.panBy(0, 160), 80);
  }

  function handleSavedFromSearch(venue: Venue) {
    setVenues(v => [...v, venue]);
    setSaveOpen(false);
    closeSearchResult();
    toast(t('venues.toast.added', { name: venue.name }), 'success');
  }

  return {
    venues, setVenues,
    pinned, setPinned,
    venuesLoading,
    geocoding,
    filter, setFilter,
    selectedCats, toggleCat, clearCats,
    legFilter, setLegFilter,
    activePin, setActivePin,
    venuePanel, setVenuePanel,
    addToItineraryVenue, setAddToItineraryVenue,
    placeDetails,
    detailsLoading,
    pinScreenPos,
    cardDragOffset, setCardDragOffset,
    searchResult, setSearchResult,
    saveOpen, setSaveOpen,
    searchScreenPos,
    fsQuery, setFsQuery,
    fsPredictions,
    searchInputRef,
    venueTodays,
    filtered,
    routeUrl,
    computePinScreen,
    closeSearchResult,
    loadVenueDetails,
    onFsSelect,
    handleSavedFromSearch,
  };
}
